import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import app from "./firebase";
import LoadingComponent from "./LoadingComponent";
import { useAuth } from "./context/AuthContext";

const MAX_WORDS = 3000;

function countWords(text) {
    return text.trim() === "" ? 0 : text.trim().split(/\s+/).length;
}

export default function AddTextPage() {
    const [text, setText] = useState("");
    const [format, setFormat] = useState("qna");
    const [numQuestions, setNumQuestions] = useState(50);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { credits, currentUser, refreshCredits } = useAuth();
    const API_BASE_URL =
        process.env.REACT_APP_API_URL ||
        (window.location.hostname === "localhost" ? "http://localhost:5000" : "");

    const wordCount = countWords(text);
    const isOverLimit = wordCount > MAX_WORDS;

    // Credit cost same as PDF/PPT tier
    const creditCost = format === "paragraph" ? 200 : format === "qna" ? 250 : 300;

    const handleTextChange = (e) => {
        setText(e.target.value);
    };

    const handleProcess = async () => {
        if (!text.trim()) return alert("Please paste some text first!");
        if (isOverLimit)
            return alert(`Text exceeds ${MAX_WORDS} words. Please reduce it.`);
        if (wordCount < 30) return alert("Please paste at least 30 words of text.");

        setLoading(true);
        try {
            const auth = getAuth(app);
            const user = auth.currentUser;
            if (!user) {
                alert("Please log in to use this feature");
                setLoading(false);
                return;
            }
            const token = await user.getIdToken();

            const response = await fetch(`${API_BASE_URL}/api/process-text`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ text, format, numQuestions }),
            });

            const contentType = response.headers.get("content-type") || "";
            if (!contentType.includes("application/json")) {
                throw new Error(`Server error (${response.status}). Please try again.`);
            }
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || "Processing failed");
            if (!data.transcript) throw new Error("No result received from server");

            if (refreshCredits) refreshCredits();

            navigate("/preview", {
                state: {
                    transcript: data.transcript,
                    qna_result: data.qna_result || [],
                    paragraph_result: data.paragraph_result || "",
                    mcq_result: data.mcq_result || [],
                    fileName: "Text_Paste",
                    format: format,
                    youtubeUrl: null,
                },
            });
        } catch (err) {
            console.error("Text processing error:", err);
            alert(`Something went wrong: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container" style={{ textAlign: "center", marginTop: "30px" }}>
            <h1>📝 Add Text</h1>
            <p style={{ color: "var(--text-muted)", marginBottom: "20px" }}>
                Paste any text (up to{" "}
                <strong style={{ color: "var(--secondary-color)" }}>{MAX_WORDS} words</strong>) and
                generate AI-powered notes, Q&amp;A, or MCQs instantly.
            </p>

            {!loading ? (
                <div className="glass-container" style={{ maxWidth: "700px", margin: "0 auto", textAlign: "left" }}>

                    {/* Wallet Balance */}
                    {currentUser && (
                        <div
                            style={{
                                background:
                                    "linear-gradient(135deg, rgba(108,92,231,0.2), rgba(142,68,173,0.2))",
                                border: "1px solid var(--primary-color)",
                                borderRadius: "10px",
                                padding: "15px",
                                marginBottom: "20px",
                                textAlign: "center",
                                fontWeight: "bold",
                                color: "#fff",
                            }}
                        >
                            💰 Wallet Balance: {credits !== null ? credits : "—"} Credits
                        </div>
                    )}

                    {/* Textarea */}
                    <div style={{ marginBottom: "20px" }}>
                        <label htmlFor="text-input">
                            Paste Your Text:
                        </label>
                        <textarea
                            id="text-input"
                            rows={12}
                            placeholder="Paste your text content here... (lecture notes, articles, book chapters, etc.)"
                            value={text}
                            onChange={handleTextChange}
                            style={{
                                width: "100%",
                                padding: "16px 20px",
                                fontSize: "0.95rem",
                                fontFamily: "inherit",
                                color: "#fff",
                                background: "rgba(0, 0, 0, 0.3)",
                                border: `1px solid ${isOverLimit ? "#e74c3c" : "var(--glass-border)"}`,
                                borderRadius: "16px",
                                outline: "none",
                                resize: "vertical",
                                lineHeight: "1.6",
                                transition: "all 0.3s ease",
                                boxSizing: "border-box",
                            }}
                        />
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginTop: "6px",
                                fontSize: "0.82rem",
                            }}
                        >
                            <span style={{ color: "var(--text-muted)" }}>
                                Minimum 30 words required
                            </span>
                            <span
                                style={{
                                    color: isOverLimit
                                        ? "#e74c3c"
                                        : wordCount > MAX_WORDS * 0.85
                                            ? "orange"
                                            : "var(--secondary-color)",
                                    fontWeight: "600",
                                }}
                            >
                                {wordCount} / {MAX_WORDS} words
                            </span>
                        </div>
                    </div>

                    {/* Output Format */}
                    <div style={{ marginBottom: "20px" }}>
                        <label>Output Format:</label>
                        <div
                            className="radio-group"
                            style={{
                                display: "flex",
                                gap: "15px",
                                justifyContent: "center",
                                flexWrap: "wrap",
                            }}
                        >
                            <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                <input
                                    type="radio"
                                    value="qna"
                                    checked={format === "qna"}
                                    onChange={(e) => setFormat(e.target.value)}
                                />
                                Q&amp;A (10 Comprehensive Pairs)
                            </label>
                            <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                <input
                                    type="radio"
                                    value="paragraph"
                                    checked={format === "paragraph"}
                                    onChange={(e) => setFormat(e.target.value)}
                                />
                                Paragraph Summary
                            </label>
                            <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                <input
                                    type="radio"
                                    value="mcq"
                                    checked={format === "mcq"}
                                    onChange={(e) => setFormat(e.target.value)}
                                />
                                MCQ
                            </label>
                        </div>

                        {format === "mcq" && (
                            <div
                                style={{
                                    marginTop: "15px",
                                    padding: "10px",
                                    background: "rgba(255,255,255,0.05)",
                                    borderRadius: "8px",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                }}
                            >
                                <label
                                    style={{ display: "block", marginBottom: "8px", fontSize: "0.9rem" }}
                                >
                                    Number of Questions: <strong>{numQuestions}</strong>
                                </label>
                                <input
                                    type="range"
                                    min="10"
                                    max="100"
                                    step="10"
                                    value={numQuestions}
                                    onChange={(e) => setNumQuestions(parseInt(e.target.value))}
                                    style={{ width: "100%", accentColor: "var(--primary-color)" }}
                                />
                            </div>
                        )}
                    </div>

                    {/* Submit */}
                    <button
                        className="btn-primary"
                        onClick={handleProcess}
                        disabled={!text.trim() || isOverLimit || loading}
                        style={{
                            width: "100%",
                            background:
                                "linear-gradient(135deg, var(--primary-color), var(--accent-pink))",
                        }}
                    >
                        Generate Notes — {creditCost} Credits
                    </button>

                    {isOverLimit && (
                        <p
                            style={{
                                color: "#e74c3c",
                                textAlign: "center",
                                marginTop: "10px",
                                fontSize: "0.9rem",
                            }}
                        >
                            ⚠️ Text exceeds {MAX_WORDS} word limit. Please reduce your content.
                        </p>
                    )}
                </div>
            ) : (
                <LoadingComponent />
            )}
        </div>
    );
}
